/* eslint-disable react/require-default-props */
import { useEffect } from 'react';

import { ColoredCircularProgress } from '../../../../../../components/UI/Typography';
import { Constants } from '../../../../../../constants/hiveNotices';
import { TeamHintText, TeamMemberLoadingBox } from '../styles';
import useCriteriaDocuments from '../useCriteriaDocuments';
import { MemberEngineeringDocuments } from './MemberEngineeringDocuments';

interface EngineeringCriteriaSectionsProps {
  noticeCompanyId: number;
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleApproveWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
  disabled: boolean;
  hasOpenChangeRequest?: boolean;
}

// Engineering analysis with NO team: while the company is not approved the
// responsible's seed record does not exist yet (`seed_responsible` runs on
// approval), so `GET /team` comes back empty. The company's documents still
// need a verdict — they show up here under the COMPANY scope, with no
// accordion, the way it was before the split per person.
export function EngineeringCriteriaSections({
  noticeCompanyId,
  docLoading,
  handleReplace,
  handleApprove,
  handleApproveWithReservation,
  handleReject,
  disabled,
  hasOpenChangeRequest,
}: EngineeringCriteriaSectionsProps): JSX.Element {
  const { documents, loading, fetchDocuments } =
    useCriteriaDocuments(noticeCompanyId);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeCompanyId]);

  // Every action reloads the page through the handler and re-reads these
  // documents, so the statuses reflect what was just decided.
  const refreshAfter = (action: () => Promise<void>): Promise<void> =>
    action().then(() => fetchDocuments());

  if (loading && !documents) {
    return (
      <TeamMemberLoadingBox>
        <ColoredCircularProgress size={28} />
      </TeamMemberLoadingBox>
    );
  }

  if (!documents) {
    return <TeamHintText>{Constants.memberDocumentsLoadError}</TeamHintText>;
  }

  return (
    <MemberEngineeringDocuments
      data={documents}
      docLoading={docLoading || loading}
      handleReplace={(docId) => refreshAfter(() => handleReplace(docId))}
      handleApprove={(docId) => refreshAfter(() => handleApprove(docId))}
      handleApproveWithReservation={(reservationNote, docId) =>
        refreshAfter(() => handleApproveWithReservation(reservationNote, docId))
      }
      handleReject={(reason, docId) =>
        refreshAfter(() => handleReject(reason, docId))
      }
      disabled={disabled}
      hasOpenChangeRequest={hasOpenChangeRequest}
    />
  );
}
